import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import LoginPage from './components/LoginPage';
import DiagnosticForm from './components/DiagnosticForm';
import DiagnosticSummary from './components/DiagnosticSummary';
import CompleteDiagnosticReport from './components/CompleteDiagnosticReport';
// Authentication imports commented out - login flow disabled
// import { AuthProvider, useAuth } from './context/AuthContext';
import { AuthProvider } from './context/AuthContext';
import diagnosticService, { DiagnosticResult, MessageTestResult } from './services/diagnosticService';
import { Box, Typography, CircularProgress, Avatar } from '@mui/material';
import { Build } from '@mui/icons-material';
import ProgressIndicator from './components/ProgressIndicator';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Login flow disabled - commenting out authentication logic
// const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
//   const { isAuthenticated } = useAuth();
//   return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
// };

// Simplified route that bypasses authentication
const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

const DiagnosticTool: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [diagnosticData, setDiagnosticData] = useState<{
    yourEmail: string;
    yourNumber: string;
    destinationNumber: string;
  } | null>(null);
  const [diagnosticResults, setDiagnosticResults] = useState<DiagnosticResult[]>([]);
  const [messageTestResult, setMessageTestResult] = useState<MessageTestResult | undefined>(undefined);
  const [ticketId, setTicketId] = useState<string>('');

  const handleStartDiagnostics = async (data: { yourEmail: string; yourNumber: string; destinationNumber: string }) => {
    setDiagnosticData(data);
    setTicketId(diagnosticService.generateTicketId());
    setCurrentStep(2);

    try {
      // Run all diagnostics with both numbers
      const results = await diagnosticService.runAllDiagnostics(data.yourNumber, data.destinationNumber);
      setDiagnosticResults(results);
      
      // Create Zendesk ticket after all diagnostics complete (Step 3)
      if (results && results.length > 0) {
        console.log('🎫 Creating Zendesk ticket after diagnostics complete (Step 3)');
        const zendeskResult = await diagnosticService.createZendeskTicket(
          data.yourNumber, 
          results, 
          undefined, // No message test result at Step 3
          data.yourEmail, 
          data.destinationNumber
        );
        
        if (zendeskResult.success) {
          console.log('✅ Zendesk ticket created successfully:', zendeskResult.ticketId);
        } else {
          console.warn('⚠️ Failed to create Zendesk ticket:', zendeskResult.error);
        }
      }
      
      setCurrentStep(3);
    } catch (error) {
      console.error('Error running diagnostics:', error);
      setCurrentStep(3);
    }
  };

  const handleRunMessageTest = async () => {
    if (!diagnosticData) return;

    try {
      // Get messagingAppId from diagnostic results
      const numberMappingResult = diagnosticResults.find(result => result.title === 'Number Mapping Check');
      
      // Check if number mapping exists
      const isNumberMapped = numberMappingResult?.status === 'success';
      const isNumberNotFound = numberMappingResult?.data?.errorCode === 404;
      
      if (isNumberNotFound) {
        // Number not found in any database - skip actual message sending
        console.log('⚠️ Number not mapped in system, skipping actual message test');
        
        const result = {
          status: 'failed' as const,
          messageId: `MSG-${Date.now()}`,
          deliveryTime: '0 seconds',
          // carrier: 'Unknown',
          timestamp: new Date().toLocaleString(),
          error: 'Number not mapped in system - cannot send test message',
          skipped: true
        };
        
        // Zendesk ticket already created at Step 3 - no need to create again
        console.log('ℹ️ Skipping Zendesk ticket creation - already created at Step 3');
        
        setMessageTestResult(result);
        setCurrentStep(4);
        return;
      }
      
      if (!isNumberMapped) {
        // Number mapping check failed for other reasons (500 errors, etc.)
        console.log('⚠️ Number mapping check failed, but proceeding with test message');
      }
      
      const messagingAppId = numberMappingResult?.data?.userId || 'nrvmodi@gmail.com';
      
      // Extract first email from messagingAppId (comma-separated)
      const sentBy = messagingAppId.split(',')[0].trim();
      
      // Create diagnostic test message
      let message = `This is diagnostic test message from ${diagnosticData.yourNumber}.`;
      if(diagnosticData.destinationNumber){
        message = `This is diagnostic test message from ${diagnosticData.yourNumber} to ${diagnosticData.destinationNumber}.`;
      }

      const result = await diagnosticService.runMessageTest({
        fromNumber: diagnosticData.yourNumber,
        toNumber: diagnosticData.destinationNumber,
        message: message,
        groupChannelId: '', // Backend now constructs this automatically
        platformType: 'Teams',
        sentBy: sentBy,
      }, diagnosticResults, diagnosticData.yourEmail); // Pass diagnostic results and user email for Zendesk ticket creation
      
      setMessageTestResult(result);
      setCurrentStep(4);
    } catch (error) {
      console.error('Error running message test:', error);
      setMessageTestResult({
        status: 'failed',
        messageId: `MSG-${Date.now()}`,
        deliveryTime: '0 seconds',
        // carrier: 'Unknown',
        timestamp: new Date().toLocaleString(),
      });
      setCurrentStep(4);
    }
  };

  const handleTestAnotherNumber = () => {
    setCurrentStep(1);
    setDiagnosticData(null);
    setDiagnosticResults([]);
    setMessageTestResult(undefined);
    setTicketId('');
  };

  const handleDownloadReport = async () => {
    try {
      // Create a simple text report for now
      const reportContent = `
Messaging Diagnostic Report
==========================
Ticket: ${ticketId}
User Email: ${diagnosticData?.yourEmail || 'Not provided'}
Phone Number: ${diagnosticData?.yourNumber}
Destination Number: ${diagnosticData?.destinationNumber}
Date: ${new Date().toLocaleString()}

Diagnostic Results:
${diagnosticResults.map(result => 
  `${result.title}: ${result.status.toUpperCase()} - ${result.message}`
).join('\n')}

${messageTestResult ? `
Message Test Results:
Status: ${messageTestResult.status.toUpperCase()}
Message ID: ${messageTestResult.messageId}
Delivery Time: ${messageTestResult.deliveryTime}
Carrier: ${messageTestResult.carrier}
Timestamp: ${messageTestResult.timestamp}
` : ''}
      `;

      // Create and download file
      const blob = new Blob([reportContent], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `diagnostic-report-${ticketId}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading report:', error);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return <DiagnosticForm onStartDiagnostics={handleStartDiagnostics} />;
      case 2:
        return (
          <Box sx={{ 
            minHeight: '100vh', 
            backgroundColor: '#f5f5f5',
            color: 'text.primary'
          }}>
            <ProgressIndicator currentStep={2} steps={['Input', 'Diagnostics', 'Results', 'Report']} />
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              flex: 1,
              padding: { xs: 2, md: 4 },
            }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      backgroundColor: 'primary.main',
                      boxShadow: '0 8px 24px rgba(25, 118, 210, 0.3)',
                    }}
                  >
                    <Build sx={{ fontSize: 40, color: 'white' }} />
                  </Avatar>
                </Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 2,
                  }}
                >
                  Running Diagnostics...
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'text.secondary',
                    mb: 3,
                  }}
                >
                  Please wait while we analyze your messaging configuration
                </Typography>
              </Box>
              <CircularProgress 
                size={60} 
                thickness={4}
                sx={{ 
                  color: 'primary.main',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  },
                }}
              />
            </Box>
          </Box>
        );
      case 3:
        return (
          <DiagnosticSummary
            phoneNumber={diagnosticData?.yourNumber || ''}
            destinationNumber={diagnosticData?.destinationNumber || ''}
            ticketId={ticketId}
            diagnosticResults={diagnosticResults}
            onRunMessageTest={handleRunMessageTest}
            onTestAnotherNumber={handleTestAnotherNumber}
          />
        );
      case 4:
        return (
          <CompleteDiagnosticReport
            phoneNumber={diagnosticData?.yourNumber || ''}
            userEmail={diagnosticData?.yourEmail || ''}
            ticketId={ticketId}
            diagnosticResults={diagnosticResults}
            messageTestResult={messageTestResult}
            onTestAnotherNumber={handleTestAnotherNumber}
            onDownloadReport={handleDownloadReport}
          />
        );
      default:
        return <Navigate to="/diagnostic" />;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {renderCurrentStep()}
    </Box>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Login route disabled - commenting out for now */}
            {/* <Route path="/login" element={<LoginPage />} /> */}
            
            {/* Dashboard route removed - users go directly to diagnostic tool */}
            <Route 
              path="/diagnostic" 
              element={
                <PrivateRoute>
                  <DiagnosticTool />
                </PrivateRoute>
              } 
            />
            <Route path="/" element={<Navigate to="/diagnostic" />} />
            <Route path="/dashboard" element={<Navigate to="/diagnostic" />} />
            {/* Redirect login attempts to diagnostic page */}
            <Route path="/login" element={<Navigate to="/diagnostic" />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App; 
